import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';

import { logoutPatientPortal } from '../../api/patientPortal/auth.ts';
import { LogoLockup } from '../../components/Logo.tsx';
import { Button } from '../../components/primitives.tsx';
import { DateTimeFormatMenu } from '../../format/DateTimeFormatMenu.tsx';
import { ThemeMenu } from '../../theme/ThemeMenu.tsx';
import { PortalLanguageMenu } from '../i18n/PortalLanguageMenu.tsx';
import { PATIENT_SESSION_QUERY_KEY, usePatientSession } from '../session/PatientSessionContext.ts';

/**
 * The patient portal's own shell — deliberately not `shell/AppShell.tsx` reused or parameterized.
 * `docs/decisions/patient-portal-frontend-scope.md`'s "base layout" call: a patient's nav has
 * nothing in common with a clinician's (five fixed destinations, no capability/entitlement gating,
 * no org white-label logo, no help-chat widget wired to staff-side chart context) — a lighter,
 * purpose-built shell rather than a reused one with most of `AppShell`'s own machinery disabled.
 *
 * **One nav, always rendered, not conditionally mounted for mobile vs. desktop the way `AppShell`'s
 * nav rail is.** `AppShell.tsx`'s own header explains why *its* nav needs that discipline: a
 * multi-item vertical rail that would otherwise need a hamburger drawer at phone width, and a
 * CSS-toggled dual DOM produces duplicate ids. This shell has five destinations total, short enough
 * to wrap as a single horizontal pill row at any width with no drawer and no second copy of
 * anything in the document — genuinely simpler, not a shortcut around the same hazard.
 *
 * Every design-system token used here (`bg-canvas`, `text-ink`, `border-line`, `rounded-control`,
 * …) is the same one `AppShell.tsx`/`primitives.tsx` already define, matching the existing staff
 * app's visual system per this task's own "branding" call — light/dark and the accent picker both
 * come for free through `ThemeMenu`, which reads the same root-level `ThemeProvider` every route in
 * this app shares.
 */
export function PortalShell() {
  const { t } = useTranslation('portalShell');
  const { orgId } = useParams<{ orgId: string }>();
  const session = usePatientSession();

  if (orgId === undefined) {
    throw new Error('PortalShell rendered outside a /portal/:orgId route');
  }

  return (
    <div className="flex flex-1 flex-col bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-control focus:bg-surface focus:px-3 focus:py-2 focus:text-ink"
      >
        {t('skipToContent')}
      </a>

      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex w-full max-w-4xl flex-wrap items-center gap-3 px-4 py-3">
          <LogoLockup size={24} />
          <span className="min-w-0 flex-1 truncate text-sm text-ink-muted">{t('title')}</span>

          <span className="hidden text-sm text-ink-muted sm:inline">
            {t('signedInAs', { name: `${session.firstName} ${session.lastName}` })}
          </span>
          <DateTimeFormatMenu />
          <ThemeMenu />
          <PortalLanguageMenu />
          <SignOutButton />
        </div>

        <nav aria-label={t('navLabel')} className="border-t border-line px-4 py-2">
          <PortalNavList orgId={orgId} />
        </nav>
      </header>

      <div className="mx-auto w-full max-w-4xl flex-1 px-4 py-4 sm:py-6">
        <main id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function PortalNavList({ orgId }: { readonly orgId: string }) {
  const { t } = useTranslation('portalShell');
  const location = useLocation();

  const items: readonly {
    readonly to: string;
    readonly labelKey: string;
    readonly end: boolean;
  }[] = [
    { to: `/portal/${orgId}`, labelKey: 'nav.home', end: true },
    { to: `/portal/${orgId}/records`, labelKey: 'nav.records', end: false },
    { to: `/portal/${orgId}/schedule`, labelKey: 'nav.schedule', end: false },
    { to: `/portal/${orgId}/messages`, labelKey: 'nav.messages', end: false },
    { to: `/portal/${orgId}/account`, labelKey: 'nav.account', end: false },
  ];

  return (
    <ul className="m-0 flex list-none flex-wrap gap-1 p-0">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            aria-current={location.pathname === item.to ? 'page' : undefined}
            className={({ isActive }) =>
              [
                'block rounded-control px-3 py-2 text-sm font-medium',
                isActive ? 'bg-nav-active text-nav-active-ink' : 'text-ink hover:bg-raised',
              ].join(' ')
            }
          >
            {t(item.labelKey)}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/**
 * Signing out resets the cached session query, nothing more — no explicit navigation. The very
 * next render of `RequirePatientSession` (still mounted around this shell) refetches `auth/me`,
 * gets a 401, and redirects to this org's own `/portal/:orgId/login` on its own, matching
 * `AppShell.tsx`'s own `SignOutButton` in relying on `RequireAuth` for the same thing.
 */
function SignOutButton() {
  const { t } = useTranslation('portalShell');
  const queryClient = useQueryClient();
  const signOut = useMutation({
    mutationFn: logoutPatientPortal,
    // Settled, not success — matching `AppShell.tsx`'s own `SignOutButton`: a logout that errored
    // still means this browser should stop acting signed in.
    onSettled: () => {
      void queryClient.resetQueries({ queryKey: PATIENT_SESSION_QUERY_KEY });
    },
  });

  return (
    <Button
      variant="secondary"
      pending={signOut.isPending}
      onClick={() => {
        signOut.mutate();
      }}
    >
      {signOut.isPending ? t('signOut.pending') : t('signOut.action')}
    </Button>
  );
}
